import { useMemo, useCallback } from 'react';
import { useTheme } from 'styled-components';
import { useApi } from '@/api';
import { HeatmapSkeleton, LinkButton } from '@/components';
import { useIsMobile, HeatmapActivity } from '@blockanalitica/ui';
import { hexToRgb } from '@/utils';

interface ActivityProps {
  url: string;
  linkTo: string;
  outlierMultiplier?: number;
  title?: string;
}

export default function Activity({
  url,
  linkTo,
  outlierMultiplier,
  title = 'Activity'
}: ActivityProps) {
  const isMobile = useIsMobile();
  const daysAgo = isMobile ? 120 : 365;
  const [data, error] = useApi({ url });

  const theme = useTheme();
  const heatColor = useMemo(
    () => hexToRgb(theme.colors.success as string),
    [theme.colors.success]
  );

  const calculations = useMemo(() => {
    if (!data || (data && data.length === 0)) return null;

    const sortedData = [...data].sort(
      (a, b) => Number(a.value) - Number(b.value)
    );
    const q1 = sortedData[Math.floor(sortedData.length / 4)];
    const q2 = sortedData[Math.floor(sortedData.length / 2)];
    const q3 = sortedData[Math.floor((3 * sortedData.length) / 4)];
    const iqr = q3.value - q1.value;
    const outlierMultiplierValue = outlierMultiplier ?? 1.5;

    return {
      quartiles: { q1, q2, q3 },
      outliersThresholds: {
        lower: Math.max(0, q1.value - 1.5 * iqr),
        upper: q3.value + outlierMultiplierValue * iqr
      }
    };
  }, [data, outlierMultiplier]);

  const computeHeatColor = useCallback(
    (value: number) => {
      const { quartiles, outliersThresholds } = calculations;

      if (value === 0) {
        return '';
      }
      if (
        value <= outliersThresholds.lower ||
        value >= outliersThresholds.upper
      ) {
        return value <= outliersThresholds.lower
          ? `rgba(${heatColor.r}, ${heatColor.g}, ${heatColor.b}, 0.1)`
          : `rgba(${heatColor.r}, ${heatColor.g}, ${heatColor.b}, 1)`;
      } else if (value < quartiles.q1.value) {
        return `rgba(${heatColor.r}, ${heatColor.g}, ${heatColor.b}, 0.2)`;
      } else if (value < quartiles.q2.value) {
        return `rgba(${heatColor.r}, ${heatColor.g}, ${heatColor.b}, 0.4)`;
      } else if (value < quartiles.q3.value) {
        return `rgba(${heatColor.r}, ${heatColor.g}, ${heatColor.b}, 0.6)`;
      } else {
        return `rgba(${heatColor.r}, ${heatColor.g}, ${heatColor.b}, 0.8)`;
      }
    },
    [calculations, heatColor]
  );
  const options = useMemo(() => {
    if (
      !calculations ||
      calculations.quartiles.q1.value === 0 ||
      (calculations.quartiles.q1.value === 1 &&
        calculations.quartiles.q2.value === 0) ||
      calculations.quartiles.q2.value === 1
    ) {
      return {};
    } else {
      return { computeHeatColor };
    }
  }, [calculations, computeHeatColor]);
  if (error) throw error;

  if (!data) {
    return <HeatmapSkeleton />;
  }
  return (
    <HeatmapActivity
      title={title}
      action={
        <LinkButton variant="secondary" to={linkTo}>
          view all
        </LinkButton>
      }
      data={data}
      daysAgo={daysAgo}
      options={options}
    />
  );
}
